import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Users, MousePointerClick } from "lucide-react";

function StatCard({ label, value, testid }) {
    return (
        <Card className="border-[#123B5D]/10" data-testid={testid}>
            <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-widest text-[#123B5D]/60">{label}</CardTitle></CardHeader>
            <CardContent><div className="text-2xl font-bold text-[#123B5D]">{value}</div></CardContent>
        </Card>
    );
}

function BarChart({ data, height = 140 }) {
    const max = Math.max(1, ...data.map((d) => d.count));
    return (
        <div className="flex items-end gap-1" style={{ height }} data-testid="analytics-bar-chart">
            {data.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
                    <div className="w-full rounded-t-sm bg-[#0F766E]/80" style={{ height: `${Math.max(4, (d.count / max) * 100)}%` }} />
                    <div className="text-[9px] text-[#17212B]/40 mt-1 truncate">{d.date.slice(5)}</div>
                </div>
            ))}
        </div>
    );
}

export default function Analytics() {
    const [summary, setSummary] = useState(null);
    const [doctor, setDoctor] = useState(null);
    useEffect(() => {
        Promise.all([
            api.get("/me/analytics/summary"),
            api.get("/me/doctor"),
        ]).then(([s, d]) => { setSummary(s.data); setDoctor(d.data); })
          .catch((e) => toast.error(errMsg(e, "Failed to load")));
    }, []);
    if (!summary) return <DoctorShell><div className="text-[#17212B]/60">Loading analytics…</div></DoctorShell>;

    const cta = summary.cta_breakdown || {};

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="analytics-page-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Insights</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Analytics</h1>
                <p className="text-[#17212B]/60 mt-1 text-sm">Aggregated visits, QR scans and CTA clicks from your public page.</p>
            </div>

            {summary.empty ? (
                <Card className="border-[#123B5D]/10"><CardContent className="py-16 text-center text-[#17212B]/60" data-testid="analytics-empty-state">
                    <TrendingUp size={40} className="mx-auto text-[#0F766E] mb-3" />
                    <div className="font-semibold text-[#123B5D]">No data yet</div>
                    <div className="text-sm mt-2 max-w-md mx-auto">
                        Once your public page starts getting visits, you'll see views, QR scans, referrers and CTA clicks tracked here — updated in real time.
                    </div>
                </CardContent></Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <StatCard label="Total views" value={summary.views_total} testid="an-views-total" />
                        <StatCard label="Views (7 days)" value={summary.views_7d} testid="an-views-7d" />
                        <StatCard label="Views (30 days)" value={summary.views_30d} testid="an-views-30d" />
                        <StatCard label="QR scans" value={summary.qr_scans_total} testid="an-qr" />
                    </div>

                    <Card className="border-[#123B5D]/10 mb-6">
                        <CardHeader className="pb-2"><CardTitle className="text-sm">Daily page views · last 14 days</CardTitle></CardHeader>
                        <CardContent><BarChart data={summary.daily || []} /></CardContent>
                    </Card>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="border-[#123B5D]/10" data-testid="an-cta-breakdown">
                            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><MousePointerClick size={14} /> CTA clicks</CardTitle></CardHeader>
                            <CardContent>
                                <ul className="text-sm divide-y divide-[#123B5D]/5">
                                    {[
                                        ["Book Appointment", "cta_book"],
                                        ["Call", "cta_call"],
                                        ["WhatsApp", "cta_whatsapp"],
                                        ["Get Directions", "cta_directions"],
                                    ].map(([label, key]) => (
                                        <li key={key} className="py-2 flex justify-between">
                                            <span>{label}</span>
                                            <span className="font-semibold text-[#123B5D]">{cta[key] || 0}</span>
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>

                        <Card className="border-[#123B5D]/10" data-testid="an-engagement">
                            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users size={14} /> Engagement</CardTitle></CardHeader>
                            <CardContent>
                                <ul className="text-sm divide-y divide-[#123B5D]/5">
                                    <li className="py-2 flex justify-between"><span>Total CTA clicks</span><span className="font-semibold text-[#123B5D]">{summary.cta_clicks_total}</span></li>
                                    <li className="py-2 flex justify-between"><span>Click-through rate (est.)</span><span className="font-semibold text-[#123B5D]">{summary.views_total ? `${Math.round(100 * summary.cta_clicks_total / summary.views_total)}%` : "—"}</span></li>
                                    <li className="py-2 flex justify-between"><span>QR scan share</span><span className="font-semibold text-[#123B5D]">{summary.views_total ? `${Math.round(100 * summary.qr_scans_total / (summary.views_total + summary.qr_scans_total))}%` : "—"}</span></li>
                                </ul>
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
        </DoctorShell>
    );
}
